import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { canAccessRoute } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth.store';
import type { DashboardAccessContext } from '@/api/endpoints/dashboard';
import { useDashboardAccessContext } from '../hooks/useDashboardAccessContext';

/**
 * Inputs for {@link resolveLandingTarget}. `contextUserId` is the user the
 * cached access-context was fetched for — it must match the session user.
 */
export type LandingTargetInput = {
  userId: number;
  role: string;
  isAuthenticated: boolean;
  hasToken: boolean;
  data: DashboardAccessContext | undefined;
  isError: boolean;
  isFetching: boolean;
  fetchStatus: string;
  isSuccess: boolean;
  /** User id on the access-context query key. Must match `userId` (FR-90). */
  contextUserId: number;
};

/**
 * Picks `/dashboard` or `/properties` from the impersonated (or direct) user's
 * access-context. Returns null while that user's context is still loading.
 *
 * FR-90 — never dispatch from another user's cached row (the SSI operator's
 * prefetch key after impersonation handoff).
 */
export function resolveLandingTarget(input: LandingTargetInput): string | null {
  const {
    userId,
    role,
    isAuthenticated,
    hasToken,
    data,
    isError,
    isFetching,
    fetchStatus,
    isSuccess,
    contextUserId,
  } = input;
  if (userId <= 0 || (!hasToken && !isAuthenticated)) return null;
  // US-DVU-05: vault-only users land on Drawing Vault (gates in ProtectedRoute
  // — mustChangePassword / name capture — take precedence over this redirect).
  if (role === 'DrawingVaultUser') return '/drawing-vault';
  // FR-90 — SSI-keyed prefetch must not drive an impersonated client's landing.
  if (contextUserId !== userId) return null;
  // Initial load only — background refetch must not suppress dispatch.
  if (!data && (isFetching || fetchStatus === 'fetching')) return null;
  if (isError) return '/properties';
  if (!isSuccess || !data) return null;
  const preferred = data.dashboardEnabled ? '/dashboard' : '/properties';
  if (canAccessRoute(role, preferred)) return preferred;
  if (canAccessRoute(role, '/properties')) return '/properties';
  return '/support';
}

/**
 * LandingRedirect — tier/role-aware landing dispatcher (Dashboard V3 PRD US-1.1).
 *
 * Waits for a fresh access-context fetch (not stale cache) before dispatching.
 * Fail-closed to `/properties` on error or when the dashboard gate is off.
 * Impersonation uses the target user's access-context, not the SSI operator's.
 */
export function LandingRedirect() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.userId ?? 0);
  const role = useAuthStore((s) => s.user?.role ?? '');
  // Supabase password sign-in can hydrate `user` via /auth/me before `accessToken`
  // is mirrored into the Zustand store (token may live only under the sb-* key
  // briefly). Gating on accessToken alone stranded DrawingVaultUser on `/` with
  // a permanent spinner (US-DVU-05). Prefer authenticated user id.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasToken = useAuthStore((s) => Boolean(s.accessToken));
  const query = useDashboardAccessContext();
  const { data, isError, isFetching, isSuccess, fetchStatus } = query;

  const target = useMemo(
    () =>
      resolveLandingTarget({
        userId,
        role,
        isAuthenticated,
        hasToken,
        data,
        isError,
        isFetching,
        fetchStatus,
        isSuccess,
        // Hook keys by the store user — after handoff that is the impersonated id.
        contextUserId: userId,
      }),
    [hasToken, isAuthenticated, userId, role, data, isFetching, fetchStatus, isError, isSuccess],
  );

  useEffect(() => {
    if (!target) return;
    navigate(target, { replace: true });
  }, [target, navigate]);

  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-muted-foreground" />
    </div>
  );
}
